import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchWrapper } from "../_helpers";
import { getCurrentDate, getPastDate } from "../utils/formatTime";

// create slice
const initialPostalMail = {
  facilityID: 0,
  inmateResidentID: "",
  managementUserID: 0,
  // `LastNameValue` and `FirstNameValue` are used to set the value for the Select field components.
  lastNameValue: 0,
  firstNameValue: 0,
  // `LastName` and `FirstName` are used as parameters in API requests to send the selected values.
  lastName: "",
  firstName: "",
  fromDate: getPastDate(6),
  toDate: getCurrentDate(),
  isApprove: 0,
  pageNo: 1,
  rowCountPerPage: 10,
  sortField: "",
};

const name = "postalMail";
const initialState = createInitialState();
const reducers = createReducers();
const extraActions = createExtraActions();

const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder) => {
    createExtraReducers(builder);
  },
});

// exports
export const postalMailActions = { ...slice.actions, ...extraActions };
export const postalMailReducer = slice.reducer;

// implementation

function createInitialState() {
  return {
    postalMail: initialPostalMail,
    initialPostalMail,
    postalMailInfo: [],
    postalMailFullList: [],
    postalMailList: [],
    facilityInPostalMail: [],
    firstNameOptions: [],
    lastNameOptions: [],
    deleteDocResponse: null,
    uploadResponse: null,
    approvedResponse: null,
    rejectedResponse: null,
    base64data: null,
    loading: false,
    error: null,
  };
}

function createReducers() {
  return {
    setPostalMail,
    clearPostalMail,
    clearpostalMailFullList,
    clearUploadResponse,
    clearDeleteResponse,
    clearDownloadFileResponse,
    clearPostalMailInfo,
    clearSearchPostalMail,
    setPostalMailList
  };

  function setPostalMail(state, action) {
    state.postalMail = action && action.payload;
  }

  function clearPostalMail(state) {
    //state.postalMail = initialPostalMail;
    state.firstNameOptions = [];
    state.lastNameOptions = [];
  }

  function clearSearchPostalMail(state) {
    state.postalMail = initialPostalMail;
  }

  function clearpostalMailFullList(state) {
    state.postalMailFullList = [];
  }

  function clearUploadResponse(state) {
    state.uploadResponse = [];
  }

  function clearDeleteResponse(state) {
    state.deleteDocResponse = [];
  }
  function clearDownloadFileResponse(state) {
    state.base64data = null;
  }
  function clearPostalMailInfo(state) {
    state.postalMailInfo = [];
  }
  function setPostalMailList(state, action) {
    state.postalMailList = action.payload;
  }
}

function createExtraActions() {
  const baseUrl = `${process.env.REACT_APP_API_URL}/ManagementUser/PostalMail`;
  const viewUrl = `${process.env.REACT_APP_API_URL}/Kiosk/KioskS3Activity`;

  return {
    LoadFacilityDropdown: LoadFacilityDropdown(),
    LoadPostalMailList: LoadPostalMailList(),
    GetPostalMailAccessibleInmateFirstName:
      GetPostalMailAccessibleInmateFirstName(),
    GetPostalMailAccessibleInmateLastName:
      GetPostalMailAccessibleInmateLastName(),
    UploadPostalMail: UploadPostalMail(),
    DeletePostalMail: DeletePostalMail(),
    GetPostalMailInfo: GetPostalMailInfo(),
    ApprovePostalMail: ApprovePostalMail(),
    RejectPostalMail: RejectPostalMail(),
    DownloadFileFromPath: DownloadFileFromPath(),
  };

  function LoadFacilityDropdown() {
    return createAsyncThunk(
      `${name}/LoadFacilityDropdown`,
      async (managementUserID) => {
        return await fetchWrapper.get(
          `${baseUrl}/GetManagementUserAccessibleFacilityInPostalMail?managementUserID=${managementUserID}`
        );
      }
    );
  }

  function LoadPostalMailList() {
    return createAsyncThunk(`${name}/LoadPostalMailList`, async (param) => {
      return await fetchWrapper.post(`${baseUrl}/LoadPostalMailList`, param);
    });
  }

  function GetPostalMailAccessibleInmateFirstName() {
    return createAsyncThunk(
      `${name}/GetPostalMailAccessibleInmateFirstName`,
      async ({ facilityID, lastName }) => {
        return await fetchWrapper.get(
          `${baseUrl}/GetPostalMailAccessibleInmateFirstName?facilityID=${facilityID}&lastName=${lastName}`
        );
      }
    );
  }

  function GetPostalMailAccessibleInmateLastName() {
    return createAsyncThunk(
      `${name}/GetPostalMailAccessibleInmateLastName`,
      async ({ facilityID, firstName }) => {
        return await fetchWrapper.get(
          `${baseUrl}/GetPostalMailAccessibleInmateLastName?facilityID=${facilityID}&firstName=${firstName}`
        );
      }
    );
  }

  function UploadPostalMail() {
    return createAsyncThunk(`${name}/UploadPostalMail`, async (param) => {
      return await fetchWrapper.post(`${baseUrl}/UploadPostalMail`, param);
    });
  }

  function DeletePostalMail() {
    return createAsyncThunk(
      `${name}/DeletePostalMail`,
      async ({ mailScanningDocumentID, modifiedBy }) => {
        return await fetchWrapper.post(
          `${baseUrl}/DeletePostalMail?mailScanningDocumentID=${mailScanningDocumentID}&modifiedBy=${modifiedBy}`
        );
      }
    );
  }

  function GetPostalMailInfo() {
    return createAsyncThunk(
      `${name}/GetPostalMailInfo`,
      async (mailScanningDocumentID) => {
        return await fetchWrapper.get(
          `${baseUrl}/GetPostalMailInfo?mailScanningDocumentID=${mailScanningDocumentID}`
        );
      }
    );
  }

  function ApprovePostalMail() {
    return createAsyncThunk(
      `${name}/ApprovePostalMail`,
      async ({ mailScanningDocumentID, modifiedBy }) => {
        return await fetchWrapper.post(
          `${baseUrl}/ApprovePostalMail?mailScanningDocumentID=${mailScanningDocumentID}&modifiedBy=${modifiedBy}`
        );
      }
    );
  }

  function RejectPostalMail() {
    return createAsyncThunk(
      `${name}/RejectPostalMail`,
      async ({ mailScanningDocumentID, modifiedBy }) => {
        return await fetchWrapper.post(
          `${baseUrl}/RejectPostalMail?mailScanningDocumentID=${mailScanningDocumentID}&modifiedBy=${modifiedBy}`
        );
      }
    );
  }

  function DownloadFileFromPath() {
    return createAsyncThunk(
      `${name}/DownloadFileFromPath`,
      // async ({ folderPath, filename }) => {
      //   return await fetchWrapper.get(
      //     `${viewUrl}/DownloadFileFromPath?folderPath=${folderPath}&filename=${filename}`
      //   );
      // }

      async ({ folderPath, filename }, thunkAPI) => {
        try {
            const blob = await fetchWrapper.get(
                `${viewUrl}/DownloadFileFromPath?folderPath=${folderPath}&filename=${filename}`,
                null,
                { isFile: true }
            );
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob); // Output: data:application/pdf;base64,...
            });
        } catch (error) {
            console.error("Error:", error);
            return thunkAPI.rejectWithValue(error.message || "Unexpected error");
        }
    }
    );
  }
}

function createExtraReducers(builder) {
  builder
    //To get the Facility Dropdown
    .addCase(extraActions.LoadFacilityDropdown.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.LoadFacilityDropdown.fulfilled, (state, action) => {
      state.loading = false;
      state.facilityInPostalMail = action.payload;
    })
    .addCase(extraActions.LoadFacilityDropdown.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })

    //To get the FisrtName Dropdown based on LastName
    .addCase(
      extraActions.GetPostalMailAccessibleInmateFirstName.pending,
      (state) => {
        state.loading = true;
        state.error = false;
      }
    )
    .addCase(
      extraActions.GetPostalMailAccessibleInmateFirstName.fulfilled,
      (state, action) => {
        state.loading = false;
        state.firstNameOptions = action.payload;
      }
    )
    .addCase(
      extraActions.GetPostalMailAccessibleInmateFirstName.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error;
      }
    )
    //To get the LastName Dropdown based on FirstName
    .addCase(
      extraActions.GetPostalMailAccessibleInmateLastName.pending,
      (state) => {
        state.loading = true;
        state.error = false;
      }
    )
    .addCase(
      extraActions.GetPostalMailAccessibleInmateLastName.fulfilled,
      (state, action) => {
        state.loading = false;
        state.lastNameOptions = action.payload;
      }
    )
    .addCase(
      extraActions.GetPostalMailAccessibleInmateLastName.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.error;
      }
    )
    //To get the PostalMailList for Grid
    .addCase(extraActions.LoadPostalMailList.pending, (state,action) => {
      let params = action.meta.arg;
      if (params?.isNoLoading) {
        state.loading = false;
      }
      else {
        state.loading = true;
      }
      state.error = false;
    })
    .addCase(extraActions.LoadPostalMailList.fulfilled, (state, action) => {
      let params = action.meta.arg;
      if (params?.exportExcel === true) {
        state.postalMailFullList = action.payload;
      } else {
        state.postalMailList = action.payload;
      }
      state.loading = false;
    })
    .addCase(extraActions.LoadPostalMailList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })

    //Upload PostalMail Response
    .addCase(extraActions.UploadPostalMail.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.UploadPostalMail.fulfilled, (state, action) => {
      state.loading = false;
      state.uploadResponse = action.payload;
    })
    .addCase(extraActions.UploadPostalMail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })

    //PostalMail Delete
    .addCase(extraActions.DeletePostalMail.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.DeletePostalMail.fulfilled, (state, action) => {
      state.loading = false;
      state.deleteDocResponse = action.payload;
    })
    .addCase(extraActions.DeletePostalMail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })

    //GetPostalMailInfo
    .addCase(extraActions.GetPostalMailInfo.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.GetPostalMailInfo.fulfilled, (state, action) => {
      state.loading = false;
      state.postalMailInfo = action.payload;
    })
    .addCase(extraActions.GetPostalMailInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })

    //Get Response for Aprrove
    .addCase(extraActions.ApprovePostalMail.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.ApprovePostalMail.fulfilled, (state, action) => {
      state.loading = false;
      state.approvedResponse = action.payload;
    })
    .addCase(extraActions.ApprovePostalMail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })
    //Get Response for Reject
    .addCase(extraActions.RejectPostalMail.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.RejectPostalMail.fulfilled, (state, action) => {
      state.loading = false;
      state.rejectedResponse = action.payload;
    })
    .addCase(extraActions.RejectPostalMail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    })
    //ViewDocument
    .addCase(extraActions.DownloadFileFromPath.pending, (state) => {
      state.loading = true;
      state.error = false;
    })
    .addCase(extraActions.DownloadFileFromPath.fulfilled, (state, action) => {
      state.loading = false;
      state.base64data = action.payload;
    })
    .addCase(extraActions.DownloadFileFromPath.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
}
